import React from "react";
import { 
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Stack, Alert, Tooltip, TextField, 
    TablePagination, Paper
} from "@mui/material";

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { formatDate, formatMoney } from "../../producer-dashboard/ProducerDashboardHelper";

type PendingPayoutInvoicesDataProps = {
    filteredInvoices: any[];
    currency: string;
    hasMultipleOperators: boolean;
    searchTerm: string;
    setSearchTerm: (val: string) => void;
    page: number;
    setPage: (val: number) => void;
    rowsPerPage: number;
    setRowsPerPage: (val: number) => void;
};

const truncateText = (str: string, length: number) => {
    if (!str) return "";
    return str.length > length ? str.substring(0, length) + "..." : str;
};

export const PendingPayoutInvoicesData = ({
    filteredInvoices, currency, hasMultipleOperators, searchTerm, setSearchTerm, page, setPage, rowsPerPage, setRowsPerPage
}: PendingPayoutInvoicesDataProps) => {
    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <TextField 
                    placeholder="Filter by Invoice ID..." 
                    size="small" 
                    value={searchTerm} 
                    onChange={e => setSearchTerm(e.target.value)} 
                    sx={{ width: 260, bgcolor: '#fff' }} 
                />
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 600, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow sx={{ "& th": { bgcolor: '#f8fafc', fontWeight: 700, borderBottom: '2px solid #e2e8f0', py: 1.5 } }}>
                            <TableCell sx={{ width: '25%', whiteSpace: 'nowrap' }}>Invoice</TableCell>
                            <TableCell sx={{ width: '15%', whiteSpace: 'nowrap' }}>Amount</TableCell>
                            {hasMultipleOperators && <TableCell sx={{ width: '35%' }}>Operator Breakdown</TableCell>}
                            <TableCell align="right" sx={{ width: hasMultipleOperators ? '25%' : '60%', whiteSpace: 'nowrap' }}>Logs</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredInvoices.length > 0 ? filteredInvoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((inv) => {
                            const detailsMap = inv.operator_details || {};
                            const pendingOps: [string, any][] = [];
                            const paidOps: [string, any][] = [];
                            let totalOpAmount = 0;

                            Object.entries(detailsMap).forEach(([opName, d]: [string, any]) => {
                                totalOpAmount += (d?.amount || 0);
                                if (d?.paid) paidOps.push([opName, d]);
                                else pendingOps.push([opName, d]);
                            });
                            
                            return (
                                <TableRow key={inv.id} hover sx={{ '& td': { borderBottom: '1px solid #f1f5f9', py: 1.5 } }}>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {inv.id}
                                            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                • {inv.edition_date ? formatDate(new Date(inv.edition_date).getTime() / 1000) : '-'}
                                            </Typography>
                                        </Typography>
                                    </TableCell>

                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        <Typography variant="body1" sx={{ fontWeight: 800, color: 'primary.main' }}>
                                            {formatMoney(totalOpAmount, currency)}
                                        </Typography>
                                    </TableCell>

                                    {hasMultipleOperators && (
                                        <TableCell>
                                            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                                                {pendingOps.map(([opName, d]) => (
                                                    <Box key={opName} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.5, px: 1, borderRadius: 1.5, bgcolor: '#fffbeb', border: `1px solid #fde68a` }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#92400e' }}>{opName}</Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e' }}>{formatMoney(d?.amount || 0, currency)}</Typography>
                                                    </Box>
                                                ))}
                                                {paidOps.map(([opName]) => (
                                                    <Box key={opName} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 0.5, px: 1, borderRadius: 1.5, bgcolor: '#f8fafc', border: `1px solid #e2e8f0` }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#94a3b8' }}>{opName}</Typography>
                                                        <CheckCircleIcon sx={{ fontSize: 12, color: '#10b981', ml: 0.5 }} />
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </TableCell>
                                    )}

                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        {inv.payout_error_log ? (
                                            <Tooltip title={inv.payout_error_log} arrow placement="left">
                                                <Alert severity="error" icon={<ErrorOutlineIcon fontSize="inherit" />} sx={{ py: 0, px: 1, cursor: 'help', display: 'inline-flex', '& .MuiAlert-message': { p: 0, py: 0.5, fontSize: '0.7rem' } }}>
                                                    {truncateText(inv.payout_error_log, hasMultipleOperators ? 30 : 80)}
                                                </Alert>
                                            </Tooltip>
                                        ) : <Typography variant="caption" color="text.disabled">-</Typography>}
                                    </TableCell>
                                </TableRow>
                            );
                        }) : (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                    No invoices found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            
            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredInvoices.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                sx={{ borderTop: '1px solid #e2e8f0' }}
            />
        </Box>
    );
};